import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { SealCheckIcon, ArrowSquareOutIcon, UsersThreeIcon, SpinnerGapIcon, SkullIcon, PencilSimpleIcon, CheckIcon, XIcon, RocketLaunchIcon, NoteIcon } from "@phosphor-icons/react";
import { fetchMyBuilders, setBuilderLabel, setBuilderNote } from "../lib/api";
import { getClientId } from "../lib/clientId";
import { FollowBuilderButton } from "../components/FollowBuilderButton";
import { QuickBuyLink } from "../components/TradeButton";
import { PLATFORMS, getTradePlatform } from "../lib/trade";

const short = (a) => `${a.slice(0, 4)}…${a.slice(-4)}`;

const ago = (ts) => {
  if (!ts) return null;
  const s = Math.floor(Date.now() / 1000 - ts);
  if (s < 60) return `${Math.max(1, s)}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const fmtMcap = (n) => {
  if (!n || n <= 0) return "—";
  if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${Math.round(n)}`;
};

const Stat = ({ label, value, color }) => (
  <div className="border border-white/10 bg-black/20 py-2.5 text-center">
    <div className="font-mono font-bold text-base" style={color ? { color } : {}}>{value}</div>
    <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">{label}</div>
  </div>
);

const VITALS = {
  alive: { label: "ALIVE", emoji: "🫀", color: "#00E599" },
  bleeding: { label: "BLEEDING", emoji: "🩸", color: "#F0A020" },
  dead: { label: "FLATLINED", emoji: "💀", color: "#FF3366" },
};

const vitalsOf = (t) => {
  if (t.nowMcap == null || t.nowMcap < 5000 || (t.peakMcap > 0 && t.nowMcap / t.peakMcap < 0.05)) return "dead";
  if (t.peakMcap > 0 && t.nowMcap / t.peakMcap < 0.35) return "bleeding";
  return "alive";
};

const LaunchChip = ({ t, creator }) => {
  const v = VITALS[vitalsOf(t)];
  const pct = t.peakMcap > 0 && t.nowMcap != null ? Math.min(100, Math.round((t.nowMcap / t.peakMcap) * 100)) : null;
  return (
    <div className="relative group/chip flex items-stretch">
      <Link
        to={`/coin/${t.mint}`}
        data-testid={`builder-launch-${t.mint}`}
        className="flex items-center gap-1.5 px-2 py-1 border border-white/10 bg-black/20 hover:border-neon/40 transition-colors font-mono text-[11px]"
      >
        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${vitalsOf(t) === "alive" ? "animate-pulse" : ""}`} style={{ background: v.color }} />
        <span className="font-bold text-white">${t.symbol || t.mint.slice(0, 4)}</span>
        <span style={{ color: t.nowMcap != null ? v.color : "#666" }}>{t.nowMcap != null ? fmtMcap(t.nowMcap) : "—"}</span>
        {t.peakMcap >= 1_000_000 && <span style={{ color: "#00E599" }}>🏃</span>}
      </Link>
      <QuickBuyLink coin={{ address: t.mint, symbol: t.symbol, creator }} testid={`builder-launch-buy-${t.mint}`} size={11} />
      {/* Pulse Check hover card */}
      <div
        data-testid={`builder-pulse-${t.mint}`}
        className="pointer-events-none opacity-0 group-hover/chip:opacity-100 transition-opacity duration-150 absolute bottom-full left-0 mb-1.5 z-30 w-56 border bg-[#070d09] p-3 shadow-[0_8px_30px_rgba(0,0,0,0.7)]"
        style={{ borderColor: v.color + "66" }}
      >
        <div className="flex items-center justify-between mb-2">
          <span className="font-heading font-bold text-sm text-white">${t.symbol || "?"}</span>
          <span className="font-mono text-[9px] px-1.5 py-0.5 font-bold" style={{ background: v.color + "1c", border: `1px solid ${v.color}`, color: v.color }}>
            {v.emoji} {v.label}
          </span>
        </div>
        <div className="space-y-1 font-mono text-[10px]">
          <div className="flex justify-between"><span className="text-muted-foreground">Peak mcap</span><span className="text-white">{fmtMcap(t.peakMcap)}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Now</span><span style={{ color: v.color }}>{t.nowMcap != null ? fmtMcap(t.nowMcap) : "no live pair"}</span></div>
          {t.ts && <div className="flex justify-between"><span className="text-muted-foreground">Launched</span><span className="text-white">{ago(t.ts)}</span></div>}
        </div>
        {pct != null && (
          <div className="mt-2">
            <div className="h-1.5 bg-white/8 overflow-hidden">
              <div className="h-full transition-all" style={{ width: `${Math.max(2, pct)}%`, background: v.color }} />
            </div>
            <div className="font-mono text-[9px] text-muted-foreground mt-1">{pct}% of peak still standing</div>
          </div>
        )}
      </div>
    </div>
  );
};

const CurrentlyDeployed = ({ builder }) => {
  const t = builder.latest;
  if (!t) return null;
  const fresh = t.ts && Date.now() / 1000 - t.ts < 6 * 3600;
  const platformLabel = (PLATFORMS[getTradePlatform()] || PLATFORMS.axiom).label;
  return (
    <div
      data-testid={`builder-current-${builder.creator}`}
      className="mb-3 border p-2.5 flex items-center gap-2.5"
      style={{ borderColor: fresh ? "#00E599" : "rgba(255,255,255,0.12)", background: fresh ? "#00E5990d" : "rgba(0,0,0,0.2)" }}
    >
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
          <RocketLaunchIcon size={11} weight="fill" className={fresh ? "text-neon" : ""} /> Currently deployed
          {fresh && (
            <span data-testid={`builder-fresh-${builder.creator}`} className="font-mono text-[9px] px-1.5 py-px font-bold text-black bg-neon animate-pulse">
              JUST LAUNCHED
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <Link to={`/coin/${t.mint}`} data-testid={`builder-current-coin-${t.mint}`} className="font-heading font-bold text-sm hover:text-neon transition-colors">
            ${t.symbol || t.mint.slice(0, 4)}
          </Link>
          <span className="font-mono text-[11px] text-muted-foreground">
            {t.nowMcap != null ? <>now <span style={{ color: VITALS[vitalsOf(t)].color }}>{fmtMcap(t.nowMcap)}</span> · </> : null}peak {fmtMcap(t.peakMcap)}
          </span>
          {t.ts && <span className="font-mono text-[11px]" style={{ color: fresh ? "#00E599" : "#8A8F98" }}>launched {ago(t.ts)}</span>}
        </div>
      </div>
      <div title={`Buy on ${platformLabel} (your chosen exchange)`}>
        <QuickBuyLink coin={{ address: t.mint, symbol: t.symbol, creator: builder.creator }} testid={`builder-current-buy-${t.mint}`} size={15} />
      </div>
    </div>
  );
};

const NameEditor = ({ cid, creator, label, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(label || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await setBuilderLabel(cid, creator, val.trim());
      toast.success(val.trim() ? `Named "${val.trim()}"` : "Name cleared");
      setEditing(false);
      onSaved();
    } catch {
      toast.error("Couldn't save name");
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1.5">
        <input
          data-testid={`builder-name-input-${creator}`}
          autoFocus
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
          placeholder="Name this builder…"
          maxLength={40}
          className="bg-black/40 border border-neon/40 px-2 py-1 text-sm font-heading font-bold text-white outline-none focus:border-neon w-[150px]"
        />
        <button data-testid={`builder-name-save-${creator}`} onClick={save} disabled={saving} className="p-1 text-neon hover:bg-neon/10" aria-label="save name">
          {saving ? <SpinnerGapIcon size={16} className="animate-spin" /> : <CheckIcon size={16} weight="bold" />}
        </button>
        <button onClick={() => { setEditing(false); setVal(label || ""); }} className="p-1 text-muted-foreground hover:text-white" aria-label="cancel">
          <XIcon size={16} weight="bold" />
        </button>
      </div>
    );
  }

  return (
    <button
      data-testid={`builder-name-edit-${creator}`}
      onClick={() => setEditing(true)}
      title={label ? "Rename this builder" : "Give this builder a name"}
      className="group/name flex items-center gap-1.5 min-w-0"
    >
      {label ? (
        <span className="font-heading font-black text-base truncate max-w-[160px]">{label}</span>
      ) : (
        <span className="font-mono text-sm text-muted-foreground italic">Name this builder</span>
      )}
      <PencilSimpleIcon size={13} className="text-muted-foreground opacity-0 group-hover/name:opacity-100 transition-opacity shrink-0" />
    </button>
  );
};

const NoteEditor = ({ cid, creator, note, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(note || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await setBuilderNote(cid, creator, val.trim());
      toast.success(val.trim() ? "Note saved" : "Note cleared");
      setEditing(false);
      onSaved();
    } catch {
      toast.error("Couldn't save note");
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1.5 mb-3">
        <NoteIcon size={13} className="text-muted-foreground shrink-0" />
        <input
          data-testid={`builder-note-input-${creator}`}
          autoFocus
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
          placeholder="Private note (e.g. insider crew, avoid…)"
          maxLength={140}
          className="flex-1 bg-black/40 border border-white/20 px-2 py-1 text-xs text-white outline-none focus:border-neon/60"
        />
        <button data-testid={`builder-note-save-${creator}`} onClick={save} disabled={saving} className="p-1 text-neon hover:bg-neon/10" aria-label="save note">
          {saving ? <SpinnerGapIcon size={14} className="animate-spin" /> : <CheckIcon size={14} weight="bold" />}
        </button>
        <button onClick={() => { setEditing(false); setVal(note || ""); }} className="p-1 text-muted-foreground hover:text-white" aria-label="cancel note">
          <XIcon size={14} weight="bold" />
        </button>
      </div>
    );
  }

  return (
    <button
      data-testid={`builder-note-edit-${creator}`}
      onClick={() => setEditing(true)}
      title={note ? "Edit note" : "Add a private note"}
      className="group/note flex items-center gap-1.5 mb-3 text-left w-full"
    >
      <NoteIcon size={13} className="text-muted-foreground shrink-0" />
      {note ? (
        <span className="text-xs text-amber-300/90 italic truncate">{note}</span>
      ) : (
        <span className="text-xs text-muted-foreground/60 italic">Add a note…</span>
      )}
      <PencilSimpleIcon size={11} className="text-muted-foreground opacity-0 group-hover/note:opacity-100 transition-opacity shrink-0" />
    </button>
  );
};

export default function MyBuilders() {
  const cid = getClientId();
  const qc = useQueryClient();
  const [sortBy, setSortBy] = useState("fresh");
  const { data, isLoading } = useQuery({
    queryKey: ["myBuilders", cid],
    queryFn: () => fetchMyBuilders(cid),
    refetchInterval: 60000,
  });
  const builders = [...(data?.builders || [])].sort((a, b) => {
    if (sortBy === "fresh") return ((b.latest?.ts || 0) - (a.latest?.ts || 0));
    if (sortBy === "hit") return ((b.successRate || 0) - (a.successRate || 0));
    if (sortBy === "runner") {
      const peak = (x) => Math.max(0, ...(x.launches || []).map((t) => t.peakMcap || 0));
      return peak(b) - peak(a);
    }
    return ((b.followedTs || 0) - (a.followedTs || 0));
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ["myBuilders", cid] });

  const SORTS = [
    ["fresh", "🚀 Freshest launch"],
    ["hit", "🎯 Hit rate"],
    ["runner", "🏃 Biggest runner"],
    ["followed", "⭐ Recently followed"],
  ];

  return (
    <div className="max-w-[1000px] mx-auto px-4 sm:px-6 py-8 space-y-6" data-testid="my-builders-page">
      <div>
        <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter flex items-center gap-2">
          <SealCheckIcon size={30} weight="fill" className="text-neon" /> My Builders
        </h1>
        <p className="text-muted-foreground mt-2 text-sm max-w-2xl leading-relaxed">
          The deployers you follow, with their full track records. <span className="text-neon">Name each builder</span> and see exactly
          what they've launched. New launches from these builders pin to the top of the{" "}
          <Link to="/" className="text-neon hover:underline">Proven Builders</Link> strip, and — if you have Telegram connected —
          ping you the instant they mint.
        </p>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-24"><SpinnerGapIcon size={30} className="animate-spin text-neon" /></div>
      ) : builders.length === 0 ? (        <div className="border border-white/10 bg-card py-20 flex flex-col items-center gap-4 text-center px-6" data-testid="my-builders-empty">
          <UsersThreeIcon size={40} className="text-muted-foreground" />
          <p className="text-muted-foreground font-mono text-sm max-w-md">
            You're not following any builders yet. Tap <span className="text-neon">Follow builder</span> on any coin's Deployer Rap Sheet
            or on the <Link to="/leaderboard" className="text-neon hover:underline">Leaderboard's Top Deployers</Link> to start.
          </p>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-1.5 flex-wrap" data-testid="builders-sort">
            <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground mr-1">Sort:</span>
            {SORTS.map(([key, label]) => (
              <button
                key={key}
                data-testid={`builders-sort-${key}`}
                onClick={() => setSortBy(key)}
                className={`px-3 py-1.5 text-xs font-semibold border transition-colors ${sortBy === key ? "bg-neon text-black border-neon" : "border-white/12 text-muted-foreground hover:text-white hover:border-white/30"}`}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 gap-4" data-testid="my-builders-list">
          {builders.map((b) => {
            const spammer = b.isSerialSpammer;
            const proven = b.isHighQualityDeployer || b.runnerCount > 0;
            const accent = spammer ? "#FF3366" : proven ? "#00E599" : "#8A8F98";
            const hit = Math.round((b.successRate || 0) * 100);
            return (
              <div key={b.creator} data-testid={`my-builder-${b.creator}`} className="border bg-card p-5" style={{ borderColor: accent + "40" }}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <NameEditor cid={cid} creator={b.creator} label={b.label} onSaved={refresh} />
                  <span className="font-mono text-[10px] px-2 py-0.5 border flex items-center gap-1 shrink-0" style={{ color: accent, borderColor: accent + "55" }}>
                    {spammer ? <><SkullIcon size={11} /> SERIAL SPAMMER</> : proven ? "⭐ PROVEN" : "UNPROVEN"}
                  </span>
                </div>
                <a href={`https://solscan.io/account/${b.creator}`} target="_blank" rel="noreferrer" className="font-mono text-xs text-muted-foreground hover:text-neon flex items-center gap-1.5 mb-3">
                  {short(b.creator)} <ArrowSquareOutIcon size={11} />
                </a>
                <NoteEditor cid={cid} creator={b.creator} note={b.note} onSaved={refresh} />
                <CurrentlyDeployed builder={b} />
                <div className="grid grid-cols-4 gap-2.5 mb-3">
                  <Stat label="Tokens" value={b.totalTokens} />
                  <Stat label="$1M+" value={b.runnerCount} color={b.runnerCount ? "#00E599" : "#888"} />
                  <Stat label="$5M+" value={b.strongRunnerCount} color={b.strongRunnerCount ? "#00E599" : "#888"} />
                  <Stat label="Hit rate" value={`${hit}%`} />
                </div>

                {b.launches?.length > 0 && (
                  <div className="mb-3" data-testid={`builder-launches-${b.creator}`}>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5 flex items-center gap-1">
                      <RocketLaunchIcon size={12} weight="fill" style={{ color: accent }} /> Their launches
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {b.launches.map((t) => <LaunchChip key={t.mint} t={t} creator={b.creator} />)}
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between gap-2">
                  <span className="text-[11px] font-mono text-muted-foreground flex items-center gap-1">
                    <UsersThreeIcon size={13} /> {b.followers} follower{b.followers === 1 ? "" : "s"}
                  </span>
                  <FollowBuilderButton creator={b.creator} size="sm" />
                </div>
              </div>
            );
          })}
          </div>
        </>
      )}
    </div>
  );
}
